import React from "react";
import { Hotspot, Popup } from "../../../../../Types";
import { Box, Flex, useReflow } from "@react-three/flex";
import * as THREE from "three";
import { useController } from "@react-three/xr";
import { Plane, Text, useTexture } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import CloseBtn from "../../FixedComponents/CloseBtn";
import { useParametersStore } from "../../../../../api/Zustand";
import {
  UseAssetsQuery,
  UseDownloadAsset,
} from "../../../../../api/calls/Queries";
import Spinner from "../../FixedComponents/Spinner";

const popWidth = 20;

const XROpenPopup = (props: { hs: Hotspot; pop: Popup }) => {
  const { hs, pop } = props;

  // CHECK VR DEVICE TYPE

  const leftController = useController("left");
  const rightController = useController("right");
  const gazeController = useController("none");

  const isInGazeMode = !!gazeController && !leftController && !rightController;

  const position = React.useMemo(() => {
    return new THREE.Vector3(hs.x, hs.y, hs.z);
  }, [hs]);

  const { tourID } = useParametersStore();

  const { data } = UseAssetsQuery(tourID, 2);

  const imageToDownload = React.useMemo(() => {
    return data?.find((one) => one.assetId === pop.imageID)?.assetKeyFull;
  }, [data, pop]);

  const { data: imageBlob, isLoading } = UseDownloadAsset(imageToDownload);

  return (
    <Flex
      scale={isInGazeMode ? 10 : 5}
      position={position}
      onUpdate={(s) => s.lookAt(0, 0, 0)}
      justifyContent="center"
      centerAnchor
      size={[popWidth, 30, 1]}
      rotation={[0, Math.PI, 0]}
    >
      {isLoading ? (
        <Spinner scale={2}></Spinner>
      ) : (
        <>
          <Box centerAnchor width={popWidth}>
            <Title>{pop.title ?? ""}</Title>
          </Box>
          <Inner imageID={imageBlob}>{pop.text ?? ""}</Inner>
        </>
      )}
    </Flex>
  );
};

export default XROpenPopup;

const textMaterial = new THREE.MeshToonMaterial({
  depthTest: false,
  color: "white",
});

const Title = (props: { children: string }) => {
  const reflow = useReflow();

  useFrame(reflow);

  return (
    <Flex
      flexDir={"row"}
      justify={"flex-start"}
      alignItems={"center"}
      centerAnchor
      size={[popWidth, 10, 0]}
    >
      <Box
        centerAnchor
        width={popWidth}
        height={10}
        margin={0}
        justify={"flex-start"}
      >
        <Text
          renderOrder={3}
          material={textMaterial}
          fontWeight={600}
          position={[-popWidth / 2, 0, 0]}
          fontSize={2}
          maxWidth={popWidth}
          textAlign="left"
          anchorX={"left"}
        >
          {props.children}
        </Text>
      </Box>

      <Box centerAnchor width={10} height={10}>
        <CloseBtn scale={1} />
      </Box>
    </Flex>
  );
};

const img = new Image();
const Inner = (props: { children: string; imageID?: string }) => {
  const reflow = useReflow();

  const [ratio, setRatio] = React.useState<number | undefined>(undefined);

  useFrame(reflow);

  React.useEffect(() => {
    setRatio(undefined);
    if (props.imageID) {
      img.src = props.imageID;
      img.onload = () => {
        setRatio(img.naturalWidth / img.naturalHeight);
      };
    }
  }, [props.imageID]);

  const isHorizontal: boolean = React.useMemo(() => {
    if (!!ratio && ratio < 1) {
      return false;
    } else return true;
  }, [ratio]);

  return (
    <Box
      marginTop={1}
      flexDir={isHorizontal ? "column-reverse" : "row-reverse"}
      width={popWidth}
      justify={"center"}
    >
      <Box width={0} centerAnchor>
        <Text
          renderOrder={3}
          material={textMaterial}
          maxWidth={isHorizontal ? popWidth : popWidth / 1.5}
          overflowWrap="break-word"
          fontWeight={500}
        >
          {props.children}
          {/* {imageBlob} */}
        </Text>
      </Box>
      <Box centerAnchor renderOrder={3}>
        {ratio && props.imageID && (
          <ImageWrapper imageBlob={props.imageID} ratio={ratio}></ImageWrapper>
        )}
      </Box>
    </Box>
  );
};

const imageScale = new THREE.Vector3();

const ImageWrapper = (props: { ratio: number; imageBlob: string }) => {
  const map = useTexture(props.imageBlob);

  const imageSize: THREE.Vector3 = React.useMemo(() => {
    if (props.ratio >= 1) {
      return imageScale.set(25, 25 / props.ratio, 1);
    } else {
      return imageScale.set(25 / props.ratio, 25, 1);

      // return { w: 20, h: 20 * props.ratio };
    }
  }, [props.ratio]);

  return (
    <Plane
      scale={imageSize}
      material={
        new THREE.MeshBasicMaterial({
          transparent: true,
          depthTest: false,
          map: map,
        })
      }
    ></Plane>
  );
};
